import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowRight, Images, ListChecks, MapPinned } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { enterLanding } from '@/shared/auth/landing';
import { useAuthStore } from '@/shared/auth/store';
import { BRAND } from '@/shared/ui/brand';

/**
 * This page is a fallback, not a destination: an account with a default page
 * expects that page when it opens the portal, exactly as after login. Bookmarks
 * and the browser's URL bar keep bringing people back here, so the check lives
 * on the route rather than only on the links pointing at it.
 */
export const Route = createFileRoute('/_auth/home')({
  beforeLoad: () => enterLanding(useAuthStore.getState().user, '/home'),
  component: HomePage,
});

const TILES = [
  {
    to: '/visit-monitor',
    icon: MapPinned,
    titleKey: 'DTK_COMMON_NAVBAR_VISITMONITOR',
    title: 'Visit monitor',
    description:
      'How the field team worked today: totals per agent, their visits on the map and the workflow behind each one.',
  },
  {
    to: '/visit-list',
    icon: ListChecks,
    titleKey: 'DTK_COMMON_NAVBAR_VISITLIST',
    title: 'Visits',
    description:
      'Every visit of the selected period with its documents, photos and statistics.',
  },
  {
    to: '/photo-viewer',
    icon: Images,
    titleKey: 'DTK_COMMON_NAVBAR_PHOTOVIEWER',
    title: 'Photo viewer',
    description:
      'Photos of a visit with folder navigation, fullscreen viewer and export.',
  },
] as const;

function HomePage() {
  const user = useAuthStore((s) => s.user);
  const { t } = useTranslation();

  return (
    <div className="mx-auto max-w-5xl space-y-8 py-4">
      <div className="rounded-xl bg-brand-gradient px-6 py-7 text-white shadow-sm">
        <div className="text-[11px] font-medium uppercase tracking-[0.18em] text-white/70">
          {BRAND.tagline}
        </div>
        <h1 className="mt-2 text-3xl font-bold tracking-tight">
          {t('HOME_WELCOME', { defaultValue: 'Welcome back' })}
        </h1>
        <p className="mt-1 text-sm text-white/80">
          {user?.userName ?? user?.login ?? BRAND.name}
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {TILES.map((tile) => (
          <Tile
            key={tile.to}
            to={tile.to}
            icon={tile.icon}
            title={t(tile.titleKey, { defaultValue: tile.title })}
            description={tile.description}
          />
        ))}
      </div>
    </div>
  );
}

function Tile({
  to,
  icon: Icon,
  title,
  description,
}: {
  to: string;
  icon: LucideIcon;
  title: string;
  description: string;
}) {
  return (
    <Link
      to={to}
      className="group flex h-full flex-col rounded-xl border bg-card p-5 transition-all hover:-translate-y-0.5 hover:border-brand/40 hover:shadow-md"
    >
      <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-brand-gradient text-white shadow-sm">
        <Icon className="h-5 w-5" />
      </span>
      <div className="mt-4 flex items-center gap-1.5 font-semibold">
        {title}
        <ArrowRight className="h-4 w-4 text-brand opacity-0 transition-opacity group-hover:opacity-100" />
      </div>
      <p className="mt-1.5 text-sm text-muted-foreground">{description}</p>
    </Link>
  );
}
